import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import axios from 'axios';
import { User, TenantRef, LoginRequest } from '../types';
import { authService } from '../services/authService';
import { getAuthSession, resetAuthSession } from '../lib/authSession';

/** FE-K01: Typisierte Response für Token-Refresh (statt `any`) */
interface TokenRefreshResponse {
  access_token: string;
  refresh_token?: string;
}

interface AuthState {
  // State
  user: User | null;
  tenant: TenantRef | null;
  isAuthenticated: boolean;
  token: string | null;
  refreshToken: string | null;
  isLoading: boolean;
  error: string | null;

  // Actions
  login: (credentials: LoginRequest) => Promise<void>;
  logout: () => Promise<void>; // SEC-H01: async für serverseitigen Revoke
  refreshAccessToken: () => Promise<void>;
  setUser: (user: User) => void;
  setTenant: (tenant: TenantRef) => void;
  setTokens: (accessToken: string, refreshToken: string) => void;
  clearError: () => void;
  setLoading: (loading: boolean) => void;
  initialize: () => Promise<void>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial state
      // FIX: isLoading startet als `true`, damit ProtectedRoute einen Spinner zeigt,
      // bis initialize() den Token validiert hat. Verhindert Race-Condition,
      // bei der API-Calls mit abgelaufenem Token vor der Validierung feuern.
      user: null,
      tenant: null,
      isAuthenticated: false,
      token: null,
      refreshToken: null,
      isLoading: true,
      error: null,

      // Login action
      login: async (credentials: LoginRequest) => {
        resetAuthSession();
        const session = getAuthSession();
        try {
          set({ isLoading: true, error: null });

          const response = await authService.login(credentials);
          const { access_token, refresh_token, user, tenant } = response;
          if (session.revision !== getAuthSession().revision) return;

          // FE-K04: Nur Zustand-Store als Single Source of Truth
          // persist-Middleware schreibt automatisch nach sessionStorage
          set({
            user,
            tenant,
            token: access_token,
            refreshToken: refresh_token,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });
        } catch (error) {
          if (session.revision !== getAuthSession().revision) throw error;
          set({
            error: error instanceof Error ? error.message : 'Login fehlgeschlagen',
            isLoading: false,
          });
          throw error;
        }
      },

      // R18: Hide private state immediately, then revoke the captured tokens.
      // WICHTIG: Der aktuelle refresh_token MUSS im Body mitgesendet werden, damit der
      // Server ihn explizit revoken kann (Backend-Log zeigte bisher "Logout without refresh_token in body").
      logout: async () => {
        const token = get().token; // Access Token für Authorization-Header
        const refreshTokenValue = get().refreshToken; // Refresh Token für Body-Revocation
        resetAuthSession();
        set({ user: null, tenant: null, token: null, refreshToken: null, isAuthenticated: false, error: null });
        try {
          if (token) {
            // Direct revocation uses the previous token without the interceptor.
            const apiUrl = import.meta.env.VITE_API_URL || '/api';
            const payload = refreshTokenValue
              ? { refresh_token: refreshTokenValue }
              : {};
            await axios.post(`${apiUrl}/auth/logout`, payload, {
              headers: { Authorization: `Bearer ${token}` },
              timeout: 5000, // Logout-Call nicht ewig blockieren
            });
          }
        } catch (error) {
          // Auch bei Server-Fehler (z. B. Token bereits abgelaufen) lokal aufräumen.
          console.warn('Server-side token revocation failed:', error);
        }
      },

      // Refresh token action (SEC-06: handles rotated refresh token)
      refreshAccessToken: async () => {
        const session = getAuthSession();
        try {
          const { refreshToken: currentRefreshToken } = get();
          if (!currentRefreshToken) {
            throw new Error('Kein Refresh-Token verfügbar');
          }

          // FE-K01: Typisierte Response statt `as any`-Cast
          const response: TokenRefreshResponse = await authService.refreshToken();
          if (session.revision !== getAuthSession().revision) return;
          const { access_token, refresh_token: new_refresh_token } = response;

          // FE-K01: Direkte set()-Aufrufe statt dynamisches Objekt mit `as any`
          // FE-K04: Kein manuelles sessionStorage — persist übernimmt
          if (new_refresh_token) {
            set({ token: access_token, refreshToken: new_refresh_token, error: null });
          } else {
            set({ token: access_token, error: null });
          }
        } catch (error) {
          // R18: An obsolete refresh cannot log out the new identity.
          if (session.revision === getAuthSession().revision) void get().logout();
          throw error;
        }
      },

      // Set tokens directly (called by Axios interceptor to sync state)
      setTokens: (accessToken: string, newRefreshToken: string) => {
        // FE-K04: persist schreibt automatisch nach sessionStorage
        set({
          token: accessToken,
          refreshToken: newRefreshToken,
        });
      },

      // Set user
      setUser: (user: User) => {
        if (get().user?.id !== user.id || get().user?.role !== user.role) resetAuthSession();
        set({ user });
      },

      // Set tenant — FE-K04: nur Store, persist schreibt automatisch
      setTenant: (tenant: TenantRef) => {
        if (get().tenant?.id !== tenant.id) resetAuthSession();
        set({ tenant });
      },

      // Clear error
      clearError: () => {
        set({ error: null });
      },

      // Set loading
      setLoading: (loading: boolean) => {
        set({ isLoading: loading });
      },

      // Initialize auth state (SEC-08, optimized)
      // FE-K04: persist rehydriert automatisch; initialize prüft nur Gültigkeit
      // FIX-401: isLoading wird explizit auf true gesetzt, damit ProtectedRoute
      // einen Spinner zeigt, bis der Token validiert ist.
      initialize: async () => {
        // Defensive: sicherstellen, dass isLoading true ist, auch wenn
        // ein externer Aufrufer den State zwischenzeitlich geändert hat.
        set({ isLoading: true });

        try {
          // FE-K04: Lese aus dem Store statt direkt aus sessionStorage
          const token = get().token;
          const refreshTokenValue = get().refreshToken;

          if (!token || !refreshTokenValue) {
            set({ isLoading: false, isAuthenticated: false });
            return;
          }

          // Optimistic token check - decode JWT to check expiration
          try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            const isExpired = payload.exp * 1000 < Date.now();

            if (isExpired) {
              // Token expired, try to refresh immediately
              try {
                await get().refreshAccessToken();
                // After refresh, fall through to load user data
              } catch (refreshError) {
                // Refresh failed, logout already triggered by refreshAccessToken
                console.warn('Token refresh failed during initialization:', refreshError);
                set({ isLoading: false });
                return;
              }
            }
          } catch (decodeError) {
            // Token decode failed, clear and logout
            console.warn('Token decode failed:', decodeError);
            await get().logout();
            set({ isLoading: false });
            return;
          }

          // At this point, we have a valid (or freshly refreshed) token
          // Set authenticated state
          set({
            isAuthenticated: true,
            isLoading: false, // App can start immediately!
          });

          // Load user data in background (non-blocking)
          const session = getAuthSession();
          authService.getCurrentUser()
            .then(response => {
              if (session.revision !== getAuthSession().revision) return;
              const { user, tenant } = response;
              if (get().user?.id !== user.id || get().user?.role !== user.role || get().tenant?.id !== tenant?.id) resetAuthSession();
              set({ user, tenant });
            })
            .catch(error => {
              if (session.revision !== getAuthSession().revision) return;
              // If user fetch fails, it's critical - logout user
              console.error('Failed to load user data - logging out:', error);
              get().logout();
            });

        } catch (error) {
          console.error('Auth initialization error:', error);
          set({
            error: 'Initialisierung fehlgeschlagen',
            isLoading: false,
            isAuthenticated: false,
          });
          // Ensure redirect to login on critical error
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
        }
      },
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => sessionStorage), // SEC-08: sessionStorage statt localStorage
      partialize: (state) => ({
        user: state.user,
        tenant: state.tenant,
        token: state.token,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
